import React, { useState } from 'react';
import API from '../utils/api';

const FeedbackWidget = () => {
  const [open, setOpen]       = useState(false);
  const [message, setMessage] = useState('');
  const [rating, setRating]   = useState(0);
  const [hover, setHover]     = useState(0);
  const [loading, setLoading] = useState(false);
  const [done, setDone]       = useState(false);
  const [error, setError]     = useState('');

  const reset = () => {
    setMessage('');
    setRating(0);
    setHover(0);
    setDone(false);
    setError('');
  };

  const handleClose = () => {
    setOpen(false);
    setTimeout(reset, 300);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setLoading(true);
    setError('');
    try {
      await API.post('/contact/feedback', { message, rating: rating || undefined });
      setDone(true);
      setTimeout(handleClose, 2200);
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating trigger button */}
      <button
        onClick={() => { setOpen(true); reset(); }}
        title="Share feedback"
        style={{
          position: 'fixed',
          bottom: '28px',
          right: '24px',
          zIndex: 1200,
          width: '52px',
          height: '52px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg,#6f42c1,#9b6fe8)',
          border: 'none',
          boxShadow: '0 4px 16px rgba(111,66,193,0.45)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          fontSize: '22px',
          transition: 'transform 0.15s, box-shadow 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(111,66,193,0.55)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)';   e.currentTarget.style.boxShadow = '0 4px 16px rgba(111,66,193,0.45)'; }}
      >
        💬
      </button>

      {/* Backdrop */}
      {open && (
        <div
          onClick={handleClose}
          style={{
            position: 'fixed', inset: 0, zIndex: 1300,
            background: 'rgba(0,0,0,0.35)',
            display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end',
            padding: '0 24px 96px 0',
          }}
        >
          {/* Modal panel */}
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: '340px',
              background: 'var(--card-bg)',
              borderRadius: '20px',
              boxShadow: '0 12px 48px rgba(0,0,0,0.18)',
              overflow: 'hidden',
              animation: 'feedbackSlideUp 0.22s ease',
            }}
          >
            {/* Header */}
            <div style={{ background: 'linear-gradient(135deg,#2d1b69,#6f42c1)', padding: '20px 24px 16px' }}>
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <div style={{ color: '#fff', fontWeight: 700, fontSize: '16px' }}>Share your thoughts 💬</div>
                  <div style={{ color: '#c9a8ff', fontSize: '12px', marginTop: '2px' }}>I read every message personally</div>
                </div>
                <button onClick={handleClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', fontSize: '20px', lineHeight: 1, cursor: 'pointer', padding: '0' }}>
                  ×
                </button>
              </div>
            </div>

            {/* Body */}
            <div style={{ padding: '20px 24px 24px' }}>
              {done ? (
                <div className="text-center py-3">
                  <div style={{ fontSize: '48px', marginBottom: '12px' }}>🙏</div>
                  <div style={{ color: 'var(--text-main)', fontWeight: 700, fontSize: '16px', marginBottom: '6px' }}>Thank you!</div>
                  <div style={{ color: 'var(--text-muted2)', fontSize: '13px' }}>Your feedback helps make Dreamy Bedtime better.</div>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  {/* Star rating */}
                  <div className="mb-3">
                    <div style={{ color: 'var(--text-muted2)', fontSize: '12px', fontWeight: 600, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      How are you finding it?
                    </div>
                    <div className="d-flex gap-1">
                      {[1,2,3,4,5].map(star => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setRating(star)}
                          onMouseEnter={() => setHover(star)}
                          onMouseLeave={() => setHover(0)}
                          style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '26px',
                            cursor: 'pointer',
                            padding: '0 2px',
                            color: star <= (hover || rating) ? '#f59e0b' : 'var(--border-purple2)',
                            transition: 'color 0.1s, transform 0.1s',
                            transform: star <= (hover || rating) ? 'scale(1.15)' : 'scale(1)',
                          }}
                        >
                          ★
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Message */}
                  <div className="mb-3">
                    <textarea
                      className="form-control"
                      placeholder="What's on your mind? A bug, a suggestion, or just saying hi — anything helps."
                      value={message}
                      onChange={e => setMessage(e.target.value)}
                      rows={4}
                      maxLength={2000}
                      style={{ borderRadius: '12px', fontSize: '14px', resize: 'none', border: '1.5px solid var(--border-purple)' }}
                    />
                    <div style={{ textAlign: 'right', color: 'var(--text-soft)', fontSize: '11px', marginTop: '4px' }}>
                      {message.length}/2000
                    </div>
                  </div>

                  {error && (
                    <div className="alert alert-danger py-2 small mb-3" style={{ borderRadius: '10px' }}>{error}</div>
                  )}

                  <button
                    type="submit"
                    disabled={loading || !message.trim()}
                    className="btn w-100 fw-semibold"
                    style={{
                      background: 'linear-gradient(135deg,#6f42c1,#9b6fe8)',
                      color: '#fff',
                      borderRadius: '50px',
                      fontSize: '14px',
                      padding: '10px',
                      border: 'none',
                      opacity: (!message.trim() || loading) ? 0.6 : 1,
                    }}
                  >
                    {loading
                      ? <><span className="spinner-border spinner-border-sm me-2"></span>Sending…</>
                      : 'Send feedback'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Slide-up animation */}
      <style>{`
        @keyframes feedbackSlideUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
};

export default FeedbackWidget;
